<template>
  <div class="py-6">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div class="flex justify-between items-center">
        <div>
        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">AI Agents</h1>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage your AI agents and their configurations
          </p>
        </div>
        <button
          @click="handleCreateClick"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
          </svg>
          Create Agent
        </button>
      </div>
    </div>

    <!-- Template Gallery: pre-built agents anyone can copy -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-8">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Färdiga agenter — kopiera och anpassa</h2>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Börja från en mall, justera systemprompt och färg, klart.
          </p>
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="template in templates"
          :key="template.id"
          class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 flex flex-col hover:shadow-md transition-shadow"
        >
          <div class="flex items-start gap-3">
            <div
              :class="[
                'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                {
                  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300': template.colorTheme === 'purple',
                  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300': template.colorTheme === 'blue',
                  'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300': template.colorTheme === 'light-blue',
                  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300': template.colorTheme === 'green',
                  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300': template.colorTheme === 'orange',
                  'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300': template.colorTheme === 'pink',
                  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300': template.colorTheme === 'yellow',
                  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300': template.colorTheme === 'red'
                }
              ]"
            >
              <!-- support -->
              <svg v-if="template.icon === 'support'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <!-- faq -->
              <svg v-else-if="template.icon === 'faq'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093M12 17h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <!-- booking -->
              <svg v-else-if="template.icon === 'booking'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <!-- sales -->
              <svg v-else-if="template.icon === 'sales'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <!-- hr -->
              <svg v-else-if="template.icon === 'hr'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-2.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <!-- it -->
              <svg v-else-if="template.icon === 'it'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ template.name }}</h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">{{ template.tagline }}</p>
            </div>
          </div>
          <p class="mt-3 text-sm text-gray-600 dark:text-gray-400 line-clamp-3 flex-1">
            {{ template.description }}
          </p>
          <button
            @click="handleCopyTemplate(template)"
            class="mt-4 w-full inline-flex items-center justify-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Kopiera & anpassa
          </button>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-8">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Dina agenter</h2>
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <!-- Empty State -->
      <div v-else-if="agents.length === 0" class="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <div class="px-4 py-12 sm:px-6 text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M3 13l3-8h12l3 8M3 13v4a2 2 0 002 2h14a2 2 0 002-2v-4"></path>
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No agents</h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new AI agent.</p>
          <div class="mt-6">
            <button
              @click="handleCreateClick"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              Create Agent
            </button>
          </div>
        </div>
      </div>

      <!-- Agents List -->
      <div v-else class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="agent in agents"
          :key="agent.id"
          class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ agent.name }}</h3>
              <p v-if="agent.description" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {{ agent.description }}
              </p>
              <div class="mt-4 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                <span class="flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  {{ new Date(agent.created_at).toLocaleDateString() }}
                </span>
                <span v-if="(agent.usage_count ?? 0) > 0" class="flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                  {{ agent.usage_count }} uses
                </span>
              </div>
            </div>
            <span
              :class="[
                agent.is_active
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
              ]"
            >
              {{ agent.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>
          <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
              {{ agent.system_prompt }}
            </p>
            <div class="flex flex-col gap-2">
              <div class="flex gap-2">
                <button
                  @click="handleEditClick(agent)"
                  class="flex-1 inline-flex items-center justify-center px-3 py-2 border border-blue-300 dark:border-blue-600 shadow-sm text-sm font-medium rounded-md text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  title="Edit agent"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  </svg>
                  Edit
                </button>
                <button
                  @click="handleCopyAgent(agent)"
                  class="inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  title="Kopiera till en ny agent"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                  </svg>
                </button>
                <button
                  @click="showInstallation(agent)"
                  class="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
                  </svg>
                  Installation
                </button>
                <button
                  @click="handleDeleteClick(agent)"
                  class="inline-flex items-center justify-center px-3 py-2 border border-red-300 dark:border-red-600 shadow-sm text-sm font-medium rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  title="Delete agent"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
              <button
                @click="showAccessInfo(agent)"
                class="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
                Access Agent
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Agent Modal -->
    <CreateAgentModal
      :is-open="showCreateModal"
      :editing-agent="editingAgent"
      :prefill="templatePrefill"
      @close="handleModalClose"
      @created="loadAgents"
      @updated="handleAgentUpdated"
    />

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmModal
      :is-open="showDeleteModal"
      title="Delete Agent"
      :message="`Are you sure you want to delete '${agentToDelete?.name}'? This action cannot be undone.`"
      :loading="deleting"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />

    <!-- Installation Modal -->
    <InstallationModal
      :is-open="showInstallationModal"
      :agent="selectedAgentForInstallation"
      :agent-url="installationAgentUrl"
      @close="showInstallationModal = false"
    />

    <!-- Access Agent Info Modal -->
    <div
      v-if="showAccessModal"
      class="fixed inset-0 z-[9999] overflow-y-auto"
      aria-labelledby="access-modal-title"
      role="dialog"
      aria-modal="true"
      @click.self="showAccessModal = false"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9998]"
          aria-hidden="true"
          @click="showAccessModal = false"
        ></div>

        <!-- Center modal -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full relative z-[10000]">
          <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="access-modal-title">
                  Access {{ selectedAgent?.name }}
                </h3>
                <div class="mt-4 space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Agent Access URL
                    </label>
                    <div class="flex items-center space-x-2">
                      <input
                        :value="agentAccessUrl"
                        readonly
                        class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                      />
                      <button
                        @click="copyToClipboard(agentAccessUrl)"
                        class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Token (for embedding)
                    </label>
                    <div class="flex items-center space-x-2">
                      <input
                        :value="agentToken"
                        readonly
                        :type="tokenVisible ? 'text' : 'password'"
                        class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                      />
                      <button
                        @click="toggleTokenVisibility"
                        class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <svg v-if="tokenVisible" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                        </svg>
                        <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                        </svg>
                      </button>
                      <button
                        @click="copyToClipboard(agentToken)"
                        class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Embed Code (Iframe)
                    </label>
                    <div class="flex items-center space-x-2">
                      <textarea
                        :value="embedCode"
                        readonly
                        rows="3"
                        class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-mono"
                      ></textarea>
                      <button
                        @click="copyToClipboard(embedCode)"
                        class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                        title="Copy embed code"
                      >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                      </button>
                    </div>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Lägg till denna kod på din webbplats där du vill ha agenten
                    </p>
                  </div>
                  <div class="rounded-md bg-blue-50 dark:bg-blue-900/20 p-3">
                    <p class="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Viktigt:</strong> Agenten fungerar bara på domäner du har godkänt i <strong>Approved Domains</strong>. Lägg till <code class="bg-blue-100 dark:bg-blue-900 px-1 rounded">taxijakt.se</code> innan du integrerar agenten.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <a
              :href="agentAccessUrl"
              target="_blank"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Open Agent
            </a>
            <button
              type="button"
              @click="showAccessModal = false"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { agentsService, type Agent, type CreateAgentRequest } from '../services/agents'
import CreateAgentModal from '../components/CreateAgentModal.vue'
import DeleteConfirmModal from '../components/DeleteConfirmModal.vue'
import InstallationModal from '../components/InstallationModal.vue'
import { agentTemplates, type AgentTemplate } from '../data/agentTemplates'

const route = useRoute()
const router = useRouter()

const agents = ref<Agent[]>([])
const loading = ref(false)
const showCreateModal = ref(false)
const editingAgent = ref<Agent | null>(null)
const templatePrefill = ref<Partial<CreateAgentRequest> | null>(null)
const templates = agentTemplates
const showAccessModal = ref(false)
const showDeleteModal = ref(false)
const showInstallationModal = ref(false)
const selectedAgent = ref<Agent | null>(null)
const selectedAgentForInstallation = ref<Agent | null>(null)
const agentToDelete = ref<Agent | null>(null)
const agentToken = ref('')
const agentAccessUrl = ref('')
const installationAgentUrl = ref('')
const deleting = ref(false)
const tokenVisible = ref(false)
const AI_AGENT_FRONTEND_URL = 'https://widget.deepchat.se'

// Check if we're on /agents/new route and open modal
watch(() => route.path, (path) => {
  console.log('Route path changed:', path)
  if (path === '/agents/new') {
    console.log('Opening modal from route')
    showCreateModal.value = true
  } else {
    // Close modal if we navigate away from /agents/new
    if (showCreateModal.value && path !== '/agents/new') {
      showCreateModal.value = false
    }
  }
}, { immediate: true })

// Watch modal state
watch(showCreateModal, (newValue) => {
  console.log('showCreateModal changed to:', newValue)
})

const handleCreateClick = () => {
  console.log('Create Agent button clicked, opening modal...')
  editingAgent.value = null
  templatePrefill.value = null
  showCreateModal.value = true
  console.log('showCreateModal is now:', showCreateModal.value)
  // Update URL without navigation
  router.replace('/agents/new')
}

const handleEditClick = (agent: Agent) => {
  console.log('Edit button clicked for agent:', agent)
  editingAgent.value = agent
  templatePrefill.value = null
  showCreateModal.value = true
}

const handleCopyTemplate = (template: AgentTemplate) => {
  editingAgent.value = null
  templatePrefill.value = {
    name: template.name,
    description: template.description,
    systemPrompt: template.systemPrompt,
    colorTheme: template.colorTheme,
    agentNames: [...template.agentNames]
  }
  showCreateModal.value = true
}

const handleCopyAgent = (agent: Agent) => {
  let agentNames: string[] = []
  if (agent.agent_names) {
    if (typeof agent.agent_names === 'string') {
      try {
        agentNames = JSON.parse(agent.agent_names)
      } catch (e) {
        agentNames = []
      }
    } else if (Array.isArray(agent.agent_names)) {
      agentNames = agent.agent_names
    }
  }
  editingAgent.value = null
  templatePrefill.value = {
    name: `${agent.name} (kopia)`,
    description: agent.description || '',
    systemPrompt: agent.system_prompt,
    modelId: (agent as any).model_id || undefined,
    colorTheme: agent.color_theme || 'purple',
    agentNames
  }
  showCreateModal.value = true
}

const handleModalClose = () => {
  showCreateModal.value = false
  editingAgent.value = null
  templatePrefill.value = null
  // Remove /new from URL when modal closes
  if (route.path === '/agents/new') {
    router.replace('/agents')
  }
}

const handleAgentUpdated = () => {
  console.log('Agent updated, reloading agents...')
  loadAgents()
}

const loadAgents = async () => {
  loading.value = true
  try {
    agents.value = await agentsService.getAgents()
  } catch (error) {
    console.error('Failed to load agents:', error)
  } finally {
    loading.value = false
  }
}

const handleDeleteClick = (agent: Agent) => {
  agentToDelete.value = agent
  showDeleteModal.value = true
}

const handleDeleteCancel = () => {
  if (!deleting.value) {
    showDeleteModal.value = false
    agentToDelete.value = null
  }
}

const handleDeleteConfirm = async () => {
  if (!agentToDelete.value || deleting.value) return

  deleting.value = true
  try {
    await agentsService.deleteAgent(agentToDelete.value.id)
    // Remove agent from local list
    agents.value = agents.value.filter(a => a.id !== agentToDelete.value!.id)
    showDeleteModal.value = false
    agentToDelete.value = null
  } catch (error) {
    console.error('Failed to delete agent:', error)
    alert(error instanceof Error ? error.message : 'Failed to delete agent')
  } finally {
    deleting.value = false
  }
}

const embedCode = computed(() => {
  if (!agentAccessUrl.value) return ''
  return `<iframe 
  src="${agentAccessUrl.value}"
  width="100%" 
  height="600px"
  frameborder="0"
  style="border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);"
></iframe>`
})

const showInstallation = async (agent: Agent) => {
  selectedAgentForInstallation.value = agent
  showInstallationModal.value = true
  
  try {
    // Get the agent token
    const token = await agentsService.getAgentToken()
    
    // Parse agent_names if exists
    let agentNames: string[] = []
    if (agent.agent_names) {
      if (typeof agent.agent_names === 'string') {
        try {
          agentNames = JSON.parse(agent.agent_names)
        } catch (e) {
          agentNames = []
        }
      } else if (Array.isArray(agent.agent_names)) {
        agentNames = agent.agent_names
      }
    }
    // Fallback to single name
    if (agentNames.length === 0) {
      agentNames = [agent.name || 'AI Assistent']
    }
    
    // Build the access URL with agentId, agentNames, and colorTheme
    // Note: We don't set agentName here - the widget code will select a random name
    // and pass it to the iframe URL
    const params = new URLSearchParams({
      token: token,
      agentId: agent.agent_id,
      colorTheme: agent.color_theme || 'purple'
    })
    
    // Add agentNames as JSON - widget will select random name from this list
    if (agentNames.length > 0) {
      params.set('agentNames', JSON.stringify(agentNames))
    }
    
    installationAgentUrl.value = `${AI_AGENT_FRONTEND_URL}/?${params.toString()}`
  } catch (error) {
    console.error('Failed to get agent token:', error)
    installationAgentUrl.value = ''
  }
}

const showAccessInfo = async (agent: Agent) => {
  selectedAgent.value = agent
  showAccessModal.value = true
  
  try {
    // Get the agent token
    agentToken.value = await agentsService.getAgentToken()
    // Build the access URL with agentId for specific agent
    agentAccessUrl.value = `${AI_AGENT_FRONTEND_URL}/?token=${agentToken.value}&agentId=${agent.agent_id}`
  } catch (error) {
    console.error('Failed to get agent token:', error)
    agentToken.value = 'Error loading token'
    agentAccessUrl.value = ''
  }
}

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
    // You could add a toast notification here
    alert('Copied to clipboard!')
  } catch (error) {
    console.error('Failed to copy:', error)
  }
}

const toggleTokenVisibility = () => {
  tokenVisible.value = !tokenVisible.value
}

onMounted(() => {
  loadAgents()
})
</script>
